'use client'

import CommentBlock from '@/app/[locale]/[roomId]/components/CommentBlock'
import {
  ShowMoreReplies,
  ShowReplyToggle,
} from '@/app/[locale]/[roomId]/components/CommentReplyButtons'
import { LoadingDots } from '@/components/ui/LoadingDots'
import { ReportReason } from '@/constants/comment'
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import {
  deleteCommentAction,
  postCommentAction,
  reportCommentAction,
  toggleLikeAction,
} from '@/server/lib/comment'
import { getComments } from '@/services/cms'
import type { Comment, CommentsData } from '@/types/comments'
import { useCallback, useEffect, useMemo, useState } from 'react'
import useSWRInfinite from 'swr/infinite'

const REPLY_PAGE_LIMIT = 10

const replyLikeChange = (reply: Comment, isLiked: boolean) => {
  const { is_liked, ...rest } = reply
  return {
    ...rest,
    like_count: Math.max(0, reply.like_count + (isLiked ? 1 : -1)),
    ...(isLiked && { is_liked: true }),
  }
}

interface CommentProps {
  comment: Comment
  lastCommentRef?: (node: HTMLDivElement | null) => void
  removeComment: (commentId: string) => void
  updateCommentReplyCount: (commentId: string, delta: number) => void
  updateCommentLikeCount: (commentId: string, isLiked: boolean) => void
  reportComment: (commentId: string, reason: ReportReason, detail: string) => void
}

function Comment({
  comment,
  lastCommentRef,
  removeComment,
  updateCommentReplyCount,
  updateCommentLikeCount,
  reportComment,
}: CommentProps) {
  const { roomData } = useCheckEnterRoomContext()
  const { store } = useConfigsContext()

  const [showReplies, setShowReplies] = useState(false)
  const [tempReplies, setTempReplies] = useState<Comment[]>([])

  const getKey = (pageIndex: number, previousPageData: CommentsData | null) => {
    if (comment.reply_count === tempReplies.length || !showReplies) return null
    if (pageIndex === 0 || !previousPageData) {
      return `comments?room_id=${roomData.id}&parent_id=${comment.id}&limit=${REPLY_PAGE_LIMIT}&cursor`
    } else {
      return previousPageData.pages.next
    }
  }

  const { data, isLoading, isValidating, setSize, mutate } = useSWRInfinite(
    getKey,
    (key) => {
      const urlParams = new URLSearchParams(key.split('?')[1])
      const cursor = urlParams.get('cursor') || ''
      return getComments({
        roomId: roomData.id,
        parentId: comment.id,
        cursor: cursor,
        limit: REPLY_PAGE_LIMIT,
        htcAuthKey: store?.credentials?.htcToken,
      })
    },
    { revalidateFirstPage: false },
  )

  const baseReplies: Comment[] = useMemo(() => {
    return data && data?.length > 0 ? data.flatMap((page) => page?.results || []) : []
  }, [data])

  useEffect(() => {
    if (baseReplies.length === 0) return

    const baseIds = new Set(baseReplies.map((reply) => reply.id))

    setTempReplies((prev) => {
      const hasOverlap = prev.some((reply) => baseIds.has(reply.id))
      if (hasOverlap) {
        return prev.filter((reply) => !baseIds.has(reply.id))
      }
      return prev
    })
  }, [baseReplies])

  const renderReplies: Comment[] = useMemo(() => {
    const tempIds = new Set(tempReplies.map((reply) => reply.id))
    const filteredBase = baseReplies.filter((reply) => !tempIds.has(reply.id))
    return [...filteredBase, ...tempReplies]
  }, [tempReplies, baseReplies])

  const canLoadMore = !!(
    !isLoading &&
    !isValidating &&
    data &&
    data.length > 0 &&
    data[data.length - 1]?.pages?.next
  )

  const addReply = async (value: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!value.trim() || !htcToken || !roomData?.id) return

    try {
      const created = await postCommentAction({
        roomId: roomData.id,
        parentId: comment.id,
        content: value,
        htcAuthKey: htcToken,
      })
      setTempReplies((prev) => [...prev, created])
      updateCommentReplyCount(comment.id, 1)
    } catch (err) {
      console.error('Failed to add Reply:', err)
    }
  }

  const reportReplyMutate = async (replyId: string, reason: ReportReason, detail: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = data
    try {
      mutate(
        (pages) =>
          pages?.map((page) => ({
            ...page,
            results: page?.results?.map((reply) =>
              reply.id === replyId ? { ...reply, is_reported: true } : reply,
            ),
          })),
        false,
      )
      await reportCommentAction({ commentId: replyId, htcAuthKey: htcToken, reason, detail })
    } catch (err) {
      mutate(cache, false)
      console.error('Failed to report reply:', err)
    }
  }

  const updateTempReplyLikeCount = async (replyId: string, isLiked: boolean) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = tempReplies
    try {
      setTempReplies((prev) =>
        prev.map((reply) => (reply.id === replyId ? replyLikeChange(reply, isLiked) : reply)),
      )
      await toggleLikeAction({ commentId: replyId, htcAuthKey: htcToken, isLiked })
    } catch (err) {
      setTempReplies(cache)
      throw err
    }
  }

  const updateReplyLikeCountMutate = async (replyId: string, isLiked: boolean) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = data
    try {
      mutate(
        (pages) =>
          pages?.map((page) => ({
            ...page,
            results: page?.results?.map((reply) =>
              reply.id === replyId ? replyLikeChange(reply, isLiked) : reply,
            ),
          })),
        false,
      )
      await toggleLikeAction({ commentId: replyId, htcAuthKey: htcToken, isLiked })
    } catch (err) {
      mutate(cache, false)
      throw err
    }
  }

  const updateReplyLikeCount = async (replyId: string, isLiked: boolean) => {
    const existedInTemp = tempReplies.some((reply) => reply.id === replyId)
    if (existedInTemp) {
      updateTempReplyLikeCount(replyId, isLiked)
    } else {
      updateReplyLikeCountMutate(replyId, isLiked)
    }
  }

  const removeTempReply = async (replyId: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = tempReplies
    try {
      setTempReplies((prev) => prev.filter((reply) => reply.id !== replyId))
      updateCommentReplyCount(comment.id, -1)
      await deleteCommentAction({ commentId: replyId, htcAuthKey: htcToken })
    } catch (err) {
      setTempReplies(cache)
      updateCommentReplyCount(comment.id, 1)
      console.error('Failed to delete temp reply:', err)
    }
  }

  const removeReplyMutate = async (replyId: string) => {
    const htcToken = store?.credentials?.htcToken
    if (!htcToken) return

    const cache = data
    try {
      mutate(
        (pages) =>
          pages?.map((page) => ({
            ...page,
            results: (page?.results || []).filter((reply) => reply.id !== replyId),
          })),
        false,
      )
      updateCommentReplyCount(comment.id, -1)
      await deleteCommentAction({ commentId: replyId, htcAuthKey: htcToken })
    } catch (err) {
      mutate(cache, false)
      updateCommentReplyCount(comment.id, 1)
      console.error('Failed to delete reply:', err)
    }
  }

  const removeReply = async (replyId: string) => {
    const existedInTemp = tempReplies.some((reply) => reply.id === replyId)
    if (existedInTemp) {
      removeTempReply(replyId)
    } else {
      removeReplyMutate(replyId)
    }
  }

  const toggleReplies = () => {
    setShowReplies((prev) => !prev)
  }

  const loadMore = useCallback(() => {
    if (canLoadMore) {
      setSize((prev) => prev + 1)
    }
  }, [canLoadMore, setSize])

  useEffect(() => {
    return () => {
      mutate(undefined, undefined)
    }
  }, [mutate])

  return (
    <div className="flex h-fit w-full flex-col gap-2" ref={lastCommentRef}>
      <CommentBlock
        comment={comment}
        isReply={false}
        addComment={addReply}
        removeComment={removeComment}
        updateCommentLikeCount={updateCommentLikeCount}
        reportComment={reportComment}
      />
      {!!comment?.reply_count && (
        <div className="flex flex-col gap-2 pl-10.5 md:pl-16">
          <ShowReplyToggle
            toggleReplies={toggleReplies}
            showReplies={showReplies}
            replyCount={comment.reply_count}
          />
          {showReplies && (
            <>
              <hr className="bg-vive-surface-normal border-[0.5px]" />
              <div className="mt-1 flex flex-col gap-6">
                {renderReplies.map((reply, index) => (
                  <CommentBlock
                    key={reply.id}
                    comment={reply}
                    isReply={true}
                    addComment={addReply}
                    updateCommentLikeCount={updateReplyLikeCount}
                    removeComment={removeReply}
                    reportComment={reportReplyMutate}
                  >
                    {index === renderReplies.length - 1 && canLoadMore && (
                      <ShowMoreReplies loadMore={loadMore} />
                    )}
                  </CommentBlock>
                ))}
              </div>
            </>
          )}
          {(isLoading || isValidating) && <LoadingDots size="md" />}
        </div>
      )}
    </div>
  )
}

export default Comment
